import { useEffect, useState } from "react";
import { api, type AssistantCost, type AssistantModel, type Settings as SettingsRow } from "../api.ts";
import { C, mono, sans } from "../theme.ts";
import { useTheme, type ThemeMode } from "../glassTheme.tsx";

// Settings. The model picker, the effort, and what the assistant has cost this
// month.
//
// ───────────────────────────────────────────────────────────────────────────
// The month-to-date figure is the one running total in this whole app, and it
// is allowed for one reason: it is about money, not about him. Spec §4.
//
// What must never appear beside it: a number of conversations, a number of
// messages, a busiest day, a chart, a comparison with last month, or a budget
// bar that fills up. The server does not return any of those and there is
// nothing here to compute them from — see db/migrations/0017_assistant_usage.sql.
// ───────────────────────────────────────────────────────────────────────────

const money = (usd: number) => `$${usd < 0.01 && usd > 0 ? usd.toFixed(4) : usd.toFixed(2)}`;

const monthName = (ym: string) =>
  new Date(`${ym}-01T00:00:00Z`).toLocaleDateString("en-GB", {
    month: "long", year: "numeric", timeZone: "UTC",
  });

const THEME_OPTIONS: { key: ThemeMode; label: string; note: string }[] = [
  { key: "system", label: "System", note: "follows the phone" },
  { key: "light", label: "Light", note: "" },
  { key: "dark", label: "Dark", note: "" },
];

export function Settings() {
  const { mode, setMode } = useTheme();
  const [row, setRow] = useState<SettingsRow | null>(null);
  const [models, setModels] = useState<AssistantModel[]>([]);
  const [efforts, setEfforts] = useState<string[]>([]);
  const [cost, setCost] = useState<AssistantCost | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    Promise.all([api.settings(), api.assistantOptions(), api.assistantCost()])
      .then(([s, o, c]) => {
        if (!live) return;
        setRow(s); setModels(o.models); setEfforts(o.efforts); setCost(c);
      })
      .catch((e) => live && setError(e.message));
    return () => { live = false; };
  }, []);

  const put = async (patch: Partial<SettingsRow>) => {
    setError(null);
    const before = row;
    setRow((r) => (r ? { ...r, ...patch } : r));
    try { setRow(await api.updateSettings(patch)); }
    catch (e: any) { setRow(before); setError(e.message); }
  };

  if (error && !row) return <p style={{ color: C.alert, fontSize: 13, padding: "0 18px" }}>{error}</p>;
  if (!row) return <p style={{ color: C.inkFaint, fontSize: 13, padding: "0 18px" }}>Looking…</p>;

  return (
    <div style={{ padding: "0 18px" }}>
      <Section title="APPEARANCE">
        {/* The shell's manual override, per redesign/02-system-design.md §0 —
            system by default, with light/dark pinned here. Only the new
            glass shell (ground, nav, +add) reads this; the rest of the app
            is still on its own fixed light palette until it's rebuilt. */}
        {THEME_OPTIONS.map((o) => (
          <Line key={o.key} label={o.label} note={o.note} on={mode === o.key} onClick={() => setMode(o.key)} />
        ))}
      </Section>

      <Section title="THE ASSISTANT">
        {/* Exactly three, and nothing more expensive is ever selectable. The
            list comes from the server; this file does not name a model. */}
        {models.map((m) => (
          <Line
            key={m.id}
            label={m.label}
            note={m.note}
            on={row.model === m.id}
            onClick={() => put({ model: m.id })}
          />
        ))}
      </Section>

      <Section title="EFFORT">
        {/* Low and medium. There is no third. */}
        {efforts.map((e) => (
          <Line key={e} label={e} on={row.effort === e} onClick={() => put({ effort: e })} />
        ))}
      </Section>

      {cost && (
        <Section title="COST">
          <div style={{ padding: "10px 0", borderBottom: `1px solid ${C.rule}` }}>
            <div style={{ fontSize: 22, fontWeight: 300 }}>{money(cost.total)}</div>
            <div style={{ fontFamily: mono, fontSize: 11, color: C.inkFaint, marginTop: 3 }}>
              {`${cost.currency} · ${monthName(cost.month)} so far`}
            </div>
          </div>
          {cost.by_model.map((b) => (
            <div key={b.model} style={{
              display: "flex", justifyContent: "space-between",
              padding: "9px 0", borderBottom: `1px solid ${C.rule}`,
            }}>
              <span style={{ fontSize: 13.5, color: C.inkSoft }}>
                {models.find((m) => m.id === b.model)?.label ?? b.model}
              </span>
              <span style={{ fontFamily: mono, fontSize: 12, color: C.inkFaint }}>{money(b.usd)}</span>
            </div>
          ))}
          {cost.by_model.length === 0 && (
            <p style={{ color: C.inkFaint, fontSize: 13, margin: "10px 0" }}>Nothing spent this month.</p>
          )}
        </Section>
      )}

      {error && <p style={{ color: C.alert, fontSize: 13 }}>{error}</p>}
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: 22 }}>
      <p style={{
        fontFamily: mono, fontSize: 11, color: C.inkFaint, letterSpacing: 1,
        margin: "18px 0 4px",
      }}>{title}</p>
      {children}
    </div>
  );
}

function Line(
  { label, note, on, onClick }: { label: string; note?: string; on: boolean; onClick: () => void },
) {
  return (
    <button
      onClick={onClick}
      style={{
        display: "block", width: "100%", textAlign: "left", background: "none",
        border: "none", borderBottom: `1px solid ${C.rule}`, padding: "11px 2px",
        cursor: "pointer", fontFamily: sans,
      }}
    >
      <span style={{ fontSize: 14, color: C.ink, fontWeight: on ? 600 : 400 }}>{label}</span>
      {note && (
        <span style={{ fontFamily: mono, fontSize: 11, color: C.inkFaint, marginLeft: 10 }}>{note}</span>
      )}
    </button>
  );
}
